import { useCallback, useEffect, useReducer } from 'react';
import {
  PowerBiReportDto,
  useGetOrganizationPowerBiReports,
  useGetSingleOrganizationPowerBiReport,
} from '@sib/api';
import { useAuthStore, useUserSetupStore } from '@sib/auth';

type PowerBiReportsState = {
  isLoading: boolean;
  powerBiReports: PowerBiReportDto[];
};

type PowerBiReportsAction = Partial<PowerBiReportsState>;

const initialPowerBiReportsState: PowerBiReportsState = {
  isLoading: false,
  powerBiReports: [],
};

const powerBiReportsReducer = (
  previousState: PowerBiReportsState,
  nextState: PowerBiReportsAction
): PowerBiReportsState => ({
  ...previousState,
  ...nextState,
});

export const usePowerBiReports = () => {
  const [powerBiReports, dispatch] = useReducer(
    powerBiReportsReducer,
    initialPowerBiReportsState
  );
  const { isAuthenticated } = useAuthStore();
  const organizationId = useUserSetupStore(state => state.organization.id);

  const { data } = useGetOrganizationPowerBiReports({
    organizationId: organizationId,
    options: {
      enabled: isAuthenticated,
    },
  });
  const { mutateAsync: fetchReportDetails } =
    useGetSingleOrganizationPowerBiReport();

  const fetchAllReportDetails = useCallback(async () => {
    if (!data?.items) return dispatch({ powerBiReports: [] });

    dispatch({ isLoading: true });
    const reports = await Promise.allSettled(
      data.items.map(async report => {
        const { id } = report;

        return fetchReportDetails({ reportId: id, organizationId });
      })
    );

    const fulfilledReports = reports
      .filter(
        (report): report is PromiseFulfilledResult<PowerBiReportDto> =>
          report.status === 'fulfilled'
      )
      .map(report => report.value);

    dispatch({ powerBiReports: fulfilledReports, isLoading: false });
  }, [data, fetchReportDetails, organizationId]);

  useEffect(() => {
    fetchAllReportDetails();
  }, [fetchAllReportDetails]);

  return powerBiReports;
};
